import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { createUseStyles, Icon } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import OptionGroup from './OptionGroup';

import styles from '../styles';
const useStyles = createUseStyles(styles, 'ModalOptions');

const GroupSection = ({ disabled, item, selected, key, ...otherProps }) => {
    const { id, label, schema } = item;
    const { isExpanded, options } = selected;
    const { onToggle } = otherProps;
    const classes = useStyles();
    const { t } = useWeb5Context();
    const contentRef = useRef();
    const [contentLength, setContentLength] = useState();
    const [expanded, setExpanded] = useState(isExpanded);
    const [counts, setCounts] = useState({ total: 0, selected: 0 });

    const duration = 400; // Section toggle transition duration (ms)

    useEffect(() => {
        const total = schema.reduce((acc, el) => acc + el.options.length, 0);
        const selectedCount = options
            ? Object.values(options).reduce((acc, el) => acc + el.options.length, 0)
            : 0;
        setCounts({ total, selected: selectedCount });
    }, [schema, options]);

    useEffect(() => {
        const content = contentRef.current;
        if (expanded) {
            content.style.display = 'block';
            content.style.height = 'auto';
            content.style.transition = `height ${duration}ms`;
        } else {
            content.style.height = `${content.offsetHeight}px`;
            setTimeout(() => {
                content.style.height = 0;
                setTimeout(() => {
                    content.style.display = 'none';
                }, duration);
            });
        }
    }, [expanded, duration]);

    const toggleHandler = useCallback(() => {
        setExpanded((exp) => {
            onToggle(id, !exp);
            return !exp;
        });
    }, [id, onToggle]);

    const countLiteral = useMemo(() => {
        const { total, selected } = counts;
        return total === selected
            ? `(${t('label_all_selected')})`
            : `(${t('label_number_selected', [selected])})`;
    }, [t, counts]);

    const groupSectionClasses = useMemo(
        () =>
            classnames(classes.groupSection, {
                [classes.groupSectionDisabled]: contentLength === 0 && counts.selected === 0,
            }),
        [classes, contentLength, counts.selected],
    );

    const iconName = useMemo(() => (expanded ? 'arrowUpRaw' : 'arrowDownRaw'), [expanded]);

    return (
        <div className={groupSectionClasses}>
            <div className={classes.groupSectionHeader} onClick={toggleHandler}>
                <span className={classes.groupSectionHeading}>
                    <span className={classes.groupSectionTitle}>{label}</span>
                    {counts.selected > 0 && (
                        <span className={classes.groupSectionCount}>{countLiteral}</span>
                    )}
                </span>
                <Icon
                    key={iconName}
                    className={classes.groupSectionToggle}
                    name={iconName}
                    size="raw"
                />
            </div>
            <div className={classes.groupSectionContent} ref={contentRef}>
                <OptionGroup
                    disabled={disabled}
                    schema={schema}
                    selected={options || {}}
                    sectionId={id}
                    setContentLength={setContentLength}
                    {...otherProps}
                />
            </div>
        </div>
    );
};

export default memo(GroupSection);
